import * as Sentry from '@sentry/react';
import '../sentry.config';

import React from 'react';
import ReactDOM from 'react-dom/client';

import '../fontAwesome.config';
import './main.css';

import {
  createBrowserRouter,
  RouterContextProvider,
  RouterProvider,
} from 'react-router';
import app from './app';
import env from './env';
import { initializeAppStore } from './stores/app';
import { supportedBrowsers } from 'corso-web-lib';
import { configureEmbedding } from './utils/appEmbed';
import { CORSO_URL } from './utils/constants';
import { pollForUpdates } from './utils/pollAppUpdates';
import { appStoreContext } from './utils/routing';

Sentry.setTag(
  'isUnsupportedBrowser',
  !supportedBrowsers.test(navigator.userAgent),
);
/** If `VITE_MSW` is set to any truthy value, and we're in development mode, start the mock service worker. */
async function enableMocking() {
  if (!import.meta.env.DEV) return;
  if (!env.VITE_MSW) return;

  const { worker } = await import('~/mocks/browser');
  await worker.start();
}

export const configureServices = async () => {
  if (import.meta.env.PROD) {
    pollForUpdates();
  }

  await enableMocking();
};

export const getRootElement = () => {
  const root = document.getElementById('root');

  if (!root) {
    throw new Error('#root Element Missing');
  }

  return root;
};

export const removeSplashScreenListener = (root: HTMLElement) => {
  root.addEventListener(
    'animationend',
    () => {
      const splash = document.getElementById('splash');
      if (splash) {
        document.body.removeChild(splash);
      }
    },
    { once: true },
  );

  return root;
};

export const bootstrapReact = (root: HTMLElement) => {
  const appStore = initializeAppStore();

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <RouterProvider
        router={Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)([app], {
          getContext: () => {
            const context = new RouterContextProvider();
            context.set(appStoreContext, appStore);
            return context;
          },
        })}
      />
    </React.StrictMode>,
  );
};

// * Start the application
configureServices()
  .then(getRootElement)
  .then(configureEmbedding)
  .then(removeSplashScreenListener)
  .then(bootstrapReact)
  .catch((e) => {
    console.error(e);
    window.location.href = CORSO_URL;
  });
